import { canOpenFarmApp, type SeatLike } from '../../utils/landing';
import {
  BarChart3,
  ClipboardCheck,
  Clock,
  Fuel,
  GitCompareArrows,
  LayoutDashboard,
  Radar,
  Sprout,
  TrendingUp,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';

export type AppStatus = 'available' | 'in_development';

export interface AppRegistryEntry {
  id: string;
  title: string;
  description: string;
  route?: string;
  // Secondary entry point (#1289): when set, the /apps tile renders an
  // "Editor" button (bottom-right) targeting this route, alongside the
  // primary "Öffnen →" CTA that targets ``route``. Gated by the same
  // ``flagId`` as the primary route.
  editorRoute?: string;
  // Feature-flag gate. Only consulted for ``status === 'available'`` entries
  // — in-development tiles are always rendered (without a CTA) so the
  // farmer can see what's on the roadmap.
  flagId?: string;
  // HOF-696 D6 — a FARM app: the farm switches it on (`active_farm.apps`) and the
  // seat opens it with one of ``permissionsAny`` (`active_farm.permissions`). Such
  // an entry carries no ``flagId``: flags go back to dark launches and kill
  // switches. The existing apps keep their flags until App access step 2.
  farmApp?: boolean;
  permissionsAny?: readonly string[];
  icon: LucideIcon;
  status: AppStatus;
  // Short hint shown beneath the status badge for in-dev tiles, e.g. a
  // rough timeframe or the user story the app will cover. Optional;
  // available apps don't need it.
  developmentNote?: string;
  // Per-app German prefill template for the "Feedback geben" CTA on
  // in-development tiles (#945). The literal "…" placeholder marks
  // the cursor target so the user lands ready to type.
  feedbackPrefill?: string;
}

export const APP_REGISTRY: AppRegistryEntry[] = [
  // Morgenlage (h-cockpit-daily-dashboard-redesign, successor of #2954 Cockpit)
  // — the daily briefing, deliberately FIRST: getAccessibleApps is an
  // order-preserving filter and AppsPage renders availableApps unsorted, so
  // index 0 is index 0 on screen. The ``cockpit`` id and ``apps.cockpit``
  // flag are baked into backend migration 037 — renaming THEM needs a second
  // migration, so only the visible title and the route changed
  // (/apps/cockpit redirects, see App.tsx).
  {
    id: 'cockpit',
    title: 'Morgenlage',
    description:
      'Dein täglicher Überblick am Morgen — was heute zu beachten ist, dazu Teams, Ernte, Preise, Kasse und der Ausblick auf die nächsten Tage in einer Ansicht.',
    route: '/apps/morgenlage',
    flagId: 'apps.cockpit',
    icon: LayoutDashboard,
    status: 'available',
  },
  {
    id: 'gutschrift-check',
    title: 'Gutschrift-Check',
    description:
      'Hol das Geld zurück, das Abnehmer dir zu wenig gutschreiben — jede Differenz zwischen Lieferschein und Gutschrift wird für dich gefunden und in Euro beziffert.',
    route: '/apps/gutschrift-check',
    editorRoute: '/apps/gutschrift-check/editor',
    flagId: 'apps.gutschrift_check',
    icon: GitCompareArrows,
    status: 'available',
  },
  {
    id: 'liquiditaetsplanung',
    title: 'Liquiditätsplanung',
    // NOTE (#1315): copy still promises the Gutschrift auto-inflow,
    // which is Phase 2 — v1 plans are seeded/edited by hand. Tile is
    // flag-gated to the pilot tenant only; revisit wording with Nicole
    // before any broader rollout.
    description:
      'Erfahre rechtzeitig, wenn es finanziell eng wird — deine Einnahmen aus Gutschriften fließen automatisch ein, und ein Engpass meldet sich, bevor er da ist.',
    route: '/apps/liquiditaetsplanung',
    flagId: 'apps.liquiditaetsplanung',
    icon: Wallet,
    status: 'available',
  },
  {
    id: 'deckungsbeitrag',
    title: 'Deckungsbeitrag',
    description:
      'Sieh, welche Kultur und welcher Betriebszweig dir wirklich Geld bringt — dein Deckungsbeitrag je Hektar, ehrlich gerechnet und vom stärksten zum schwächsten sortiert.',
    route: '/apps/deckungsbeitrag',
    flagId: 'apps.deckungsbeitrag',
    icon: BarChart3,
    status: 'available',
  },
  {
    id: 'ernteprognose',
    title: 'Ernteprognose',
    description:
      'Sieh früh, ob deine Ernte auf Kurs liegt — Plan, Prognose und tatsächliche Erntemengen laufen Tag für Tag nebeneinander, und ein Ampel-Hinweis meldet Rückstand, bevor die Saison vorbei ist.',
    route: '/apps/ernteprognose',
    flagId: 'apps.ernteprognose',
    icon: Sprout,
    status: 'available',
  },
  {
    id: 'preisspiegel',
    title: 'Preisspiegel',
    description:
      'Behalte die Preise im Blick, die deinen Hof bewegen — Erzeuger- und Einkaufspreise für deine Kulturen und Betriebsmittel, Kurs für Kurs, mit Verlauf und Ausblick.',
    route: '/apps/preisspiegel',
    flagId: 'apps.preisspiegel',
    icon: TrendingUp,
    status: 'available',
  },
  // HOF-696 — Arbeit (Labour MVP Pilot-5): the worker reports the day on the
  // phone, the office sees the week. The first app on farm activation (D6).
  {
    id: 'labour',
    title: 'Arbeit',
    description:
      'Sieh jeden Tag, wer woran gearbeitet hat — deine Leute tragen ihren Arbeitstag in zwei Minuten am Handy ein, und du siehst die Woche auf einen Blick.',
    route: '/apps/labour',
    farmApp: true,
    permissionsAny: ['labour.read', 'labour.self.read'],
    icon: Clock,
    status: 'available',
  },
  // ── In Entwicklung ────────────────────────────────────────────────
  // German names chosen to match what a German farm bookkeeper would
  // actually call the workflow — not literal translations of the
  // English brief. Order roughly tracks build-priority.
  {
    id: 'saisonkraefte-verwaltung',
    title: 'Saisonkräfte-Verwaltung',
    description:
      'Deine Erntehelfer sind rechtssicher gemeldet und korrekt abgerechnet — A1-Bescheinigungen, Sozialversicherung und Lohnabrechnung erledigen sich, ohne Schreibtischarbeit.',
    icon: Users,
    status: 'in_development',
    developmentNote: 'Geplant für die Erntesaison 2026',
    feedbackPrefill:
      'Ich interessiere mich für die Saisonkräfte-Verwaltung in Hofsync. Folgendes würde ich mir dort wünschen: …',
  },
  {
    id: 'agrardiesel-antrag',
    title: 'Agrardiesel-Antrag',
    description:
      'Hol dir deine Agrardiesel-Vergütung zurück — aus deinen Tankbelegen entsteht der einreichfertige Antrag fürs Hauptzollamt.',
    icon: Fuel,
    status: 'in_development',
    developmentNote: 'Konzeptphase',
    feedbackPrefill:
      'Ich interessiere mich für den Agrardiesel-Antrag in Hofsync. Folgendes würde ich mir dort wünschen: …',
  },
  {
    id: 'feldakte-pruefmappe',
    title: 'Feldakte & Prüfmappe',
    description:
      'Geh ohne Suchen durch jede Kontrolle — deine Schlagkartei sowie Pflanzenschutz- und Düngenachweise liegen prüffertig als InVeKoS-Mappe für CC- und Bio-Audits bereit.',
    icon: ClipboardCheck,
    status: 'in_development',
    developmentNote: 'Konzeptphase',
    feedbackPrefill:
      'Ich interessiere mich für die Feldakte & Prüfmappe in Hofsync. Folgendes würde ich mir dort wünschen: …',
  },
  {
    id: 'foerderung-radar',
    title: 'Förderung-Radar',
    description:
      'Verpasse keine Förderung und keine Frist mehr — du erfährst automatisch nur von den Mitteln und Gesetzesänderungen, die für deinen Hof bares Geld bedeuten.',
    icon: Radar,
    status: 'in_development',
    developmentNote: 'Konzeptphase',
    feedbackPrefill:
      'Ich interessiere mich für den Förderung-Radar in Hofsync. Folgendes würde ich mir dort wünschen: …',
  },
];

// Which available apps the current user can actually launch. Drives both
// the marketplace tile list and the "Applications" menu item visibility —
// keep per-user app gating funnelled through here so the registry stays
// the single source of truth.
//
// HOF-696 D6: a ``farmApp`` entry opens on the active farm's app list AND the
// seat's permissions; a flag never opens it. Flag-gated entries are unchanged.
export function getAccessibleApps(
  hasFlag: (flagId: string) => boolean,
  activeFarm?: SeatLike | null,
): AppRegistryEntry[] {
  return APP_REGISTRY.filter((app) => {
    if (app.status !== 'available') return false;
    if (app.farmApp) return canOpenFarmApp(activeFarm, app.id, app.permissionsAny ?? []);
    return !app.flagId || hasFlag(app.flagId);
  });
}

// The /apps gate (HOF-696 D5): an active farm app the seat may open, or the
// existing flag rule.
export function hasAccessibleApp(
  hasFlag: (flagId: string) => boolean,
  activeFarm?: SeatLike | null,
): boolean {
  return getAccessibleApps(hasFlag, activeFarm).length > 0;
}

// Flag IDs of every available app. Pass to ``<FeatureGate anyOf={...}>``
// on the /apps route so the page is reachable iff at least one app is
// enabled for the user.
export const AVAILABLE_APP_FLAGS: string[] = APP_REGISTRY.flatMap((app) =>
  app.status === 'available' && app.flagId ? [app.flagId] : [],
);
